import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Avatar,
  Box,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import { Info, Close, MoreHoriz } from "@mui/icons-material";
import dayjs from "dayjs";
import ContentEditable from "react-contenteditable";
import { firebaseHelper } from "ui";

// Style constants to avoid complex union types
const versionItemStyles = {
  display: "flex",
  alignItems: "flex-start",
  width: "100%",
};

interface FirestoreTimestamp {
  seconds: number;
  nanoseconds: number;
}

interface Version {
  versionId: string;
  versionTimestamp: FirestoreTimestamp | Date | string;
  versionName?: string;
  files?: {
    screenshot?: string;
  };
}

interface VersionHistoryModalProps {
  open: boolean;
  onClose: () => void;
  versions: Version[];
  loading: boolean;
  restoringVersion: boolean;
  onRestoreConfirmed: (version: Version) => void;
  cloudflareCDN: (url: string, options: string) => string;
  getUrlFromRef: (ref: string) => string;
  collectionName: string;
  recordId: string;
}

const VersionHistoryModal: React.FC<VersionHistoryModalProps> = ({
  open,
  onClose,
  versions,
  loading,
  restoringVersion,
  onRestoreConfirmed,
  cloudflareCDN,
  getUrlFromRef,
  collectionName,
  recordId,
}) => {
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [versionToRestore, setVersionToRestore] = useState<Version | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedVersion, setSelectedVersion] = useState<Version | null>(null);
  const [localVersions, setLocalVersions] = useState<Version[]>(versions || []);
  const [editingVersionId, setEditingVersionId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState<string>("");
  const [deletingVersion, setDeletingVersion] = useState(false);
  const [deleteConfirmDialogOpen, setDeleteConfirmDialogOpen] = useState(false);
  const [versionToDelete, setVersionToDelete] = useState<Version | null>(null);
  const editableRef = useRef<any>(null);

  useEffect(() => {
    setLocalVersions(versions || []);
  }, [versions]);

  const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>, version: Version) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedVersion(version);
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuAnchorEl(null);
    setSelectedVersion(null);
  }, []);

  const handleRevertToVersion = useCallback(() => {
    if (selectedVersion) {
      setVersionToRestore(selectedVersion);
      setConfirmDialogOpen(true);
      handleMenuClose();
    }
  }, [selectedVersion, handleMenuClose]);

  const startEditing = useCallback((version: Version) => {
    setEditingVersionId(version.versionId);
    setEditingTitle(version.versionName || "");
    // Focus slightly after render
    setTimeout(() => {
      editableRef.current?.focus?.();
    }, 0);
  }, []);

  const handleEditVersionName = useCallback(() => {
    if (selectedVersion) {
      startEditing(selectedVersion);
    }
    handleMenuClose();
  }, [selectedVersion, startEditing, handleMenuClose]);

  const handleDeleteVersion = useCallback(() => {
    if (!selectedVersion) return;
    setVersionToDelete(selectedVersion);
    setDeleteConfirmDialogOpen(true);
    handleMenuClose();
  }, [selectedVersion, handleMenuClose]);

  const handleConfirmDelete = useCallback(async () => {
    if (!versionToDelete) return;
    
    setDeleteConfirmDialogOpen(false);
    setDeletingVersion(true);
    
    try {
      const ok = await firebaseHelper.deleteVersion(
        collectionName,
        recordId,
        versionToDelete.versionId
      );
      if (ok) {
        setLocalVersions((prev) => prev.filter(v => v.versionId !== versionToDelete.versionId));
      } else {
        alert('Failed to delete version. Please try again.');
      }
    } catch (e) {
      console.error("Failed to delete version", e);
      alert('Error deleting version. Please try again.');
    } finally {
      setDeletingVersion(false);
      setVersionToDelete(null);
    }
  }, [versionToDelete, collectionName, recordId]);

  const handleConfirmRestore = () => {
    if (versionToRestore) {
      onRestoreConfirmed(versionToRestore);
      setConfirmDialogOpen(false);
      setVersionToRestore(null);
    }
  };

  const handleTitleChange = useCallback((evt: any) => {
    const v = (evt.currentTarget?.innerText || "").replace(">", "");
    const newTitle = v.substring(0, 60);
    setEditingTitle(newTitle);
  }, []);

  const finishEditing = useCallback(async (titleOverride?: string) => {
    const verId = editingVersionId;
    if (!verId) return;
    const newTitle = (titleOverride || editingTitle)?.trim() || "Untitled";
    
    try {
      const ok = await firebaseHelper.updateVersionTitle(
        collectionName,
        recordId,
        verId,
        newTitle
      );
      
      if (ok) {
        setLocalVersions((prev) => prev.map(v => (
          v.versionId === verId
            ? { ...v, versionName: newTitle }
            : v
        )));
      } else {
        alert('Failed to update version title. Please try again.');
      }
    } catch (e) {
      console.error("Failed to update version title", e);
      alert('Error updating version title. Please try again.');
    } finally {
      setEditingVersionId(null);
      setEditingTitle("");
    }
  }, [editingVersionId, editingTitle, collectionName, recordId]);

  const handleEditableBlur = useCallback((evt: any) => {
    // Get the text directly from the DOM element to ensure we capture the latest changes
    const currentText = evt.target?.innerText || editingTitle;
    finishEditing(currentText);
  }, [editingTitle, finishEditing]);

  const handleEditableKeyDown = useCallback((evt: React.KeyboardEvent) => {
    if (evt.key === "Enter") {
      evt.preventDefault();
      (evt.target as HTMLElement).blur();
    }
  }, []);

  const formatDate = (timestamp: FirestoreTimestamp | Date | string): string => {
    if (!timestamp) return "Unknown date";

    if (
      timestamp &&
      typeof timestamp === "object" &&
      "seconds" in timestamp &&
      "nanoseconds" in timestamp
    ) {
      // Convert Firestore Timestamp to JavaScript Date
      const firestoreTimestamp = timestamp as FirestoreTimestamp;
      const date = new Date(
        firestoreTimestamp.seconds * 1000 +
          firestoreTimestamp.nanoseconds / 1000000
      );
      return dayjs(date).format("MMM D YYYY [at] h:mm A");
    }

    if (timestamp instanceof Date) {
      return dayjs(timestamp).format("MMM D YYYY [at] h:mm A");
    }

    try {
      if (typeof timestamp === "string" && timestamp.includes("UTC")) {
        const date = new Date(timestamp);
        if (!isNaN(date.getTime())) {
          return dayjs(date).format("MMM D YYYY [at] h:mm A");
        }
      }

      if (typeof timestamp === "string") {
        return dayjs(timestamp).format("MMM D YYYY [at] h:mm A");
      }

      return "Invalid date format";
    } catch (error) {
      console.error("Error parsing date:", error, timestamp);
      return "Invalid date";
    }
  };

  return (
    <>
      {/* Version History Dialog */}
      <Dialog
        open={open}
        onClose={onClose}
        aria-labelledby="version-history-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle 
          id="version-history-dialog-title" 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            fontWeight: 'bold'
          }}
        >
          <span>Version History</span>
          <IconButton 
            onClick={() => setInfoDialogOpen(true)}
            size="small"
          >
            <Info fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <p>Loading...</p>
          ) : deletingVersion ? (
            <p>Deleting version...</p>
          ) : localVersions.length === 0 ? (
            <p>No saved versions yet</p>
          ) : (
            <List>
              {localVersions.map((version, index) => {
                const thumbnailUrl: string | undefined = version.files
                  ?.screenshot
                  ? cloudflareCDN(
                      getUrlFromRef(version.files.screenshot),
                      "width=180,height=135,format=auto"
                    )
                  : "none";

                return (
                  <ListItem
                    key={version.versionId}
                    divider={index < localVersions.length - 1}
                    sx={{ alignItems: "flex-start" }}
                  >
                    <div style={versionItemStyles}>
                      {/* Thumbnail */}
                      <Avatar
                        variant="rounded"
                        sx={{
                          width: 180,
                          height: 135,
                          marginRight: 2,
                          border: "1px solid #e0e0e0",
                        }}
                        src={thumbnailUrl}
                      >
                        {!version.files?.screenshot && "📷"}
                      </Avatar>

                      {/* Version Info */}
                      <div style={{ flexGrow: 1 }}>
                        {editingVersionId === version.versionId ? (
                          <ContentEditable
                            innerRef={editableRef}
                            html={editingTitle}
                            disabled={false}
                            onChange={handleTitleChange}
                            onBlur={handleEditableBlur}
                            onKeyDown={handleEditableKeyDown}
                            style={{
                              display: 'inline-block',
                              minWidth: '120px',
                              border: '1px solid #000',
                              padding: '2px 4px',
                              borderRadius: 2,
                              fontWeight: 600,
                            }}
                          />
                        ) : (
                          <ListItemText
                            primary={version.versionName || `Version ${localVersions.length - index}`}
                            secondary={formatDate(version.versionTimestamp)}
                            onClick={() => !deletingVersion && startEditing(version)}
                            sx={{ 
                              cursor: deletingVersion ? 'default' : 'pointer',
                              opacity: deletingVersion ? 0.6 : 1
                            }}
                          />
                        )}
                      </div>

                      {/* More Options Button */}
                      <ListItemSecondaryAction
                        sx={{
                          top: 8,
                          transform: 'none',
                        }}
                      >
                        <IconButton
                          onClick={(event) => handleMenuOpen(event, version)}
                          disabled={restoringVersion || deletingVersion}
                          size="small"
                          sx={{
                            color: 'black',
                          }}
                        >
                          <MoreHoriz />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </div>
                  </ListItem>
                );
              })}
            </List>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'flex-end' }}>
          <Button 
            onClick={onClose} 
            variant="outlined"
            sx={{ minWidth: '80px' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* More Options Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleRevertToVersion} disabled={deletingVersion}>
          Revert To This Version
        </MenuItem>
        <MenuItem onClick={handleEditVersionName} disabled={deletingVersion}>
          Edit Version Name
        </MenuItem>
        <MenuItem onClick={handleDeleteVersion} disabled={deletingVersion}>
          {deletingVersion ? "Deleting..." : "Delete This Version"}
        </MenuItem>
      </Menu>

      {/* Info Dialog */}
      <Dialog
        open={infoDialogOpen}
        onClose={() => setInfoDialogOpen(false)}
        aria-labelledby="info-dialog-title"
        aria-describedby="info-dialog-description"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle 
          id="info-dialog-title" 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            fontWeight: 'bold'
          }}
        >
          <span>About Version History</span>
          <IconButton 
            onClick={() => setInfoDialogOpen(false)}
            size="small"
          >
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="info-dialog-description">
            Your board is autosaved, and the last 10 saved versions are kept here. Click the more options button to restore, rename, or delete a version.
          </DialogContentText>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        aria-labelledby="confirm-restore-dialog-title"
        aria-describedby="confirm-restore-dialog-description"
      >
        <DialogTitle id="confirm-restore-dialog-title" sx={{ fontWeight: 'bold' }}>
          Revert To This Version
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-restore-dialog-description">
            Are you sure you want to revert to this version? This will replace your current board, and any unsaved changes will be lost.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'center', gap: 2 }}>
          <Button 
            onClick={() => setConfirmDialogOpen(false)} 
            variant="outlined"
            sx={{ minWidth: '120px', px: 3 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmRestore} 
            variant="contained"
            sx={{ minWidth: '120px', px: 3 }}
          >
            Revert
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmDialogOpen}
        onClose={() => setDeleteConfirmDialogOpen(false)}
        aria-labelledby="confirm-delete-dialog-title"
        aria-describedby="confirm-delete-dialog-description"
      >
        <DialogTitle id="confirm-delete-dialog-title" sx={{ fontWeight: 'bold' }}>
          Delete Version
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-delete-dialog-description">
            Are you sure you want to delete this version? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'center', gap: 2 }}>
          <Button 
            onClick={() => setDeleteConfirmDialogOpen(false)} 
            variant="outlined"
            sx={{ minWidth: '120px', px: 3 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            variant="contained"
            color="primary"
            sx={{ minWidth: '120px', px: 3 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default VersionHistoryModal;
